<script setup>
import {
  useFloating,
  offset,
  flip,
  shift,
  size as floatingSize,
  autoUpdate,
} from '@floating-ui/vue'
import { useField } from 'vee-validate'
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'

import { usePopover, useClickOutside } from '@/composables/usePopover'

const model = defineModel({ type: String })

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: false,
  },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  single: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
})

// Variáveis reativas
const currentDate = ref(new Date())
const selectedStartDate = ref(null)
const selectedEndDate = ref(null)
const canUpdate = ref(true)
const inputRef = ref(null)
const datepickerRef = ref(null)
const innerValue = ref('')

const { isOpen, close } = usePopover()

// Setup floating
// Largura máxima real do painel — calculada pelo próprio floating-ui a partir
// do espaço disponível na posição do input (considera automaticamente
// qualquer sidebar/banner lateral, ex.: o banner de 420px do onboarding a
// partir do breakpoint lg, sem depender de heurística de 100vw).
const maxAvailableWidth = ref(420)
// Altura máxima real do espaço disponível na posição final (abaixo OU acima
// do input, decidido pelo flip abaixo); se ainda assim não couber tudo, o
// próprio painel rola internamente.
const maxAvailableHeight = ref(420)

const { x, y, strategy, update } = useFloating(inputRef, datepickerRef, {
  placement: 'bottom-start',
  middleware: [
    offset(8),
    // Abre pra cima do input quando não há espaço suficiente abaixo (ex.:
    // input perto do rodapé da viewport) — sem isso o painel encolhia até
    // caber no espaço abaixo, ficando inutilizável (review PR #1967).
    flip({ fallbackPlacements: ['top-start'], padding: 8 }),
    // mainAxis ativo (default): permite o flip acima deslocar o painel;
    // crossAxis (horizontal) continua ativo para não estourar as bordas
    // laterais.
    shift({ padding: 8 }),
    floatingSize({
      padding: 8,
      apply({ availableWidth, availableHeight }) {
        maxAvailableWidth.value = Math.min(420, Math.max(280, availableWidth))
        // Sem piso: um piso de altura fixo forçava o painel a ser maior que
        // o espaço real (na posição já resolvida pelo flip), estourando a
        // viewport — o próprio painel já rola internamente quando não cabe
        // tudo.
        maxAvailableHeight.value = availableHeight
      },
    }),
  ],
})

useClickOutside([inputRef, datepickerRef], close)

// Constantes
const daysOfWeek = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']
const months = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]
const years = Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - 20 + i)
const viewMode = ref('days')

const { errorMessage } = useField(() => props.name, undefined, {
  syncVModel: true,
})

// Computed
const currentMonth = computed(() =>
  currentDate.value.toLocaleString('pt-BR', { month: 'long', year: 'numeric' }),
)

// Métodos de renderização
const renderCalendar = () => {
  const year = currentDate.value.getFullYear()
  const month = currentDate.value.getMonth()

  const firstDayOfMonth = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const daysArray = []

  for (let i = 0; i < firstDayOfMonth; i += 1) {
    daysArray.push({ day: '', className: '', dayString: '' })
  }

  for (let i = 1; i <= daysInMonth; i += 1) {
    const day = new Date(year, month, i)
    const dayString = day.toDateString()
    let className =
      'my-0.5 text-base p-0 font-medium flex w-full aspect-square max-w-[46px] items-center justify-center rounded-full hover:bg-secondary-0 cursor-pointer'

    if (
      (selectedStartDate.value &&
        !selectedEndDate.value &&
        dayString === selectedStartDate.value) ||
      (dayString === selectedEndDate.value && dayString === selectedStartDate.value)
    ) {
      className += ' bg-secondary-10 border border-secondary-50 text-secondary-50 rounded-full'
    } else {
      if (selectedStartDate.value && dayString === selectedStartDate.value) {
        className +=
          ' bg-secondary-10 border border-r-0 border-secondary-50 text-secondary-50 rounded-r-none'
      }
      if (selectedEndDate.value && dayString === selectedEndDate.value) {
        className +=
          ' bg-secondary-10 border border-l-0 border-secondary-50 text-secondary-50 rounded-l-none'
      }
      if (
        selectedStartDate.value &&
        selectedEndDate.value &&
        new Date(day) > new Date(selectedStartDate.value) &&
        new Date(day) < new Date(selectedEndDate.value)
      ) {
        className +=
          ' bg-secondary-10 border-y border-x-0 border-secondary-50 text-secondary-50 rounded-none'
      }
    }

    daysArray.push({ day: i, className, dayString })
  }

  return daysArray
}

const renderMonths = () =>
  months.map((month, index) => ({
    name: month,
    className:
      index === currentDate.value.getMonth()
        ? 'cursor-pointer w-full max-w-[122px] rounded-md flex items-center justify-center bg-secondary-50 px-2 py-2 sm:px-6 text-white font-semibold text-neutral-60 text-xs sm:text-base '
        : 'cursor-pointer w-full max-w-[122px] rounded-md flex items-center justify-center hover:bg-secondary-0 border border-neutral-30 px-2 py-2 sm:px-6 text-xs sm:text-base font-normal text-neutral-60 ',
    monthIndex: index,
  }))

const renderYears = () =>
  years.map(year => ({
    name: year,
    className:
      'cursor-pointer rounded-md flex items-center justify-center hover:bg-secondary-0 border border-neutral-30 px-2 py-2 sm:px-6 text-xs sm:text-base font-normal text-neutral-60',
  }))

// Métodos de controle
const toggleMonthView = () => {
  event.stopPropagation()
  viewMode.value = viewMode.value === 'months' ? 'days' : 'months'
}

const toggleYearView = () => {
  event.stopPropagation()
  viewMode.value = viewMode.value === 'years' ? 'days' : 'years'
}

const selectMonth = monthIndex => {
  event.stopPropagation()
  currentDate.value = new Date(currentDate.value.setMonth(monthIndex))
  viewMode.value = 'days'
}

const selectYear = year => {
  currentDate.value = new Date(currentDate.value.setFullYear(year))
  viewMode.value = 'months'
}

const handleDayClick = selectedDay => {
  event.stopPropagation()
  canUpdate.value = false

  if (props.single) {
    selectedStartDate.value = selectedDay
    selectedEndDate.value = null
  } else {
    if (!selectedStartDate.value || (selectedStartDate.value && selectedEndDate.value)) {
      selectedStartDate.value = selectedDay
      selectedEndDate.value = null
    } else {
      if (new Date(selectedDay) < new Date(selectedStartDate.value)) {
        selectedEndDate.value = selectedStartDate.value
        selectedStartDate.value = selectedDay
      } else {
        selectedEndDate.value = selectedDay
      }
    }
  }

  updateInput()
}

const updateInput = () => {
  if (props.single) {
    innerValue.value = new Date(selectedStartDate.value).toLocaleDateString('pt-BR')
  } else if (selectedStartDate.value && selectedEndDate.value) {
    innerValue.value = convertPtBrDate(selectedStartDate.value, selectedEndDate.value)
  } else if (selectedStartDate.value) {
    innerValue.value = convertPtBrDate(selectedStartDate.value, selectedStartDate.value)
  }
}

const convertIsoString = date => {
  return new Date(date).toISOString().split('T')[0]
}

const convertPtBrDate = (startDate, endDate) => {
  const brazilianStartDate = new Date(startDate).toLocaleDateString('pt-BR')
  const brazilianEndDate = new Date(endDate).toLocaleDateString('pt-BR')
  return `${brazilianStartDate} - ${brazilianEndDate}`
}

const toggleDatepicker = event => {
  event.stopPropagation()
  if (!props.disabled) {
    isOpen.value = !isOpen.value
  }
}

const handleArrowClick = direction => {
  if (viewMode.value === 'days') {
    changeMonth(direction)
  } else if (viewMode.value === 'months') {
    changeYear(direction)
  }
}

const changeMonth = direction => {
  currentDate.value = new Date(currentDate.value.setMonth(currentDate.value.getMonth() + direction))
}

const changeYear = direction => {
  currentDate.value = new Date(
    currentDate.value.setFullYear(currentDate.value.getFullYear() + direction),
  )
}

const formatInitialData = date => {
  const data = new Date(`${date}T00:00:00`)
  return data.toDateString()
}

// Auto update
let cleanup

onMounted(async () => {
  cleanup = autoUpdate(inputRef.value, datepickerRef.value, update)
  await nextTick()
  if (model.value) {
    selectedStartDate.value = formatInitialData(model.value.split(',')[0])
    selectedEndDate.value = formatInitialData(model.value.split(',')[1])
    updateInput()
  }
})

onBeforeUnmount(() => {
  if (cleanup) cleanup()
})

// Watch for open state to trigger update
watch(isOpen, async value => {
  if (value) {
    await nextTick()
    update()
  }
})

// Watchers
watch(innerValue, () => {
  if (props.single) {
    if (selectedStartDate.value) {
      model.value = convertIsoString(selectedStartDate.value)
    }
  } else {
    if (selectedStartDate.value && selectedEndDate.value) {
      model.value = `${convertIsoString(selectedStartDate.value)},${convertIsoString(selectedEndDate.value)}`
    } else if (selectedStartDate.value) {
      model.value = `${convertIsoString(selectedStartDate.value)},${convertIsoString(selectedStartDate.value)}`
    }
  }
})

watch(
  () => model.value,
  newVal => {
    if (!newVal || newVal === '') {
      selectedStartDate.value = null
      selectedEndDate.value = null
      innerValue.value = ''
      currentDate.value = new Date()
    } else {
      if (canUpdate.value) {
        if (props.single) {
          selectedStartDate.value = formatInitialData(newVal)
          selectedEndDate.value = null
        } else {
          selectedStartDate.value = formatInitialData(newVal.split(',')[0])
          selectedEndDate.value = formatInitialData(newVal.split(',')[1])
        }
        currentDate.value = new Date(selectedStartDate.value)
        updateInput()
      }
      canUpdate.value = true
    }
  },
)

const formatSingleDate = value => {
  // Remove tudo que não for número ou barra
  let clean = value.replace(/[^\d/]/g, '')

  // Divide em partes
  let [day = '', month = '', year = ''] = clean.split('/')

  // Permite digitação parcial
  if (day.length > 2) {
    month = day.slice(2) + (month || '')
    day = day.slice(0, 2)
  }
  if (month.length > 2) {
    year = month.slice(2) + (year || '')
    month = month.slice(0, 2)
  }
  if (year.length > 4) {
    year = year.slice(0, 4)
  }

  // Só valida se ambos os dígitos estiverem presentes
  if (day.length === 2 && parseInt(day) > 31) day = '31'
  if (month.length === 2 && parseInt(month) > 12) month = '12'

  // Monta o valor de volta, permitindo campos intermediários
  let result = day
  if (month.length > 0 || value.includes('/')) result += '/' + month
  if (year.length > 0 || (value.match(/\//g) || []).length > 1) result += '/' + year

  return result
}

const updateModelValue = value => {
  const datePattern = props.single
    ? /^(\d{2})\/(\d{2})\/(\d{4})$/
    : /^(\d{2})\/(\d{2})\/(\d{4})\s*-\s*(\d{2})\/(\d{2})\/(\d{4})$/

  if (!datePattern.test(value)) {
    return
  }

  const [day, month, year] = value.split('/')
  const dateString = `${year}-${month}-${day}`
  model.value = dateString
  const date = new Date(year, month - 1, day)
  currentDate.value = date
  selectedStartDate.value = date.toDateString()
  selectedEndDate.value = null
}

const handleInput = () => {
  innerValue.value = formatSingleDate(innerValue.value)
  updateModelValue(innerValue.value)
}
</script>

<template>
  <div
    class="flex w-full flex-col gap-1 px-0.5 py-0"
    :class="{
      'text-neutral-40': disabled,
    }"
  >
    <div class="relative flex flex-col gap-1">
      <label
        class="text-neutral-60"
        :class="{
          'text-base': size === 'lg',
          'text-sm': size === 'md',
          'text-xs': size === 'sm',
        }"
        :for="$attrs.id"
      >
        {{ label }}
      </label>
      <div class="flex">
        <input
          ref="inputRef"
          :placeholder="
            placeholder ? placeholder : single ? 'dd/mm/aaaa' : 'dd/mm/aaaa - dd/mm/aaaa'
          "
          class="placeholder:text-neutral-40 w-full rounded-lg px-4 pr-10"
          :class="{
            error: errorMessage,
            input: !errorMessage,
            'h-[38px]': size === 'md',
            'h-[44px]': size === 'lg',
            'h-[36px]': size === 'sm',
            'border-neutral-30': !selectedStartDate,
            'border-neutral-40': selectedStartDate,
          }"
          v-model="innerValue"
          @click="toggleDatepicker"
          @input="handleInput"
          :disabled="disabled"
          :readonly="!single"
        />
        <span
          @click="toggleDatepicker"
          class="absolute right-3 flex cursor-pointer items-center"
          :class="{
            'cursor-not-allowed': disabled,
            'h-[38px]': size === 'md',
            'h-[44px]': size === 'lg',
            'h-[36px]': size === 'sm',
          }"
        >
          <ImoIcon name="calendar" :size="20" />
        </span>
      </div>
      <span class="text-error-60 text-xs"> {{ errorMessage }} </span>
    </div>

    <Teleport to="body">
      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-class="transform scale-75 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-75 opacity-0"
      >
        <div
          v-show="isOpen"
          ref="datepickerRef"
          data-testid="date-calendar-panel"
          :style="{
            position: strategy,
            top: `${y ?? 0}px`,
            left: `${x ?? 0}px`,
            zIndex: 9999,
            width: `${maxAvailableWidth}px`,
            maxHeight: `${maxAvailableHeight}px`,
          }"
          class="border-neutral-30 flex flex-col gap-2.5 overflow-auto rounded-xl border bg-white p-3 shadow-lg sm:p-5"
        >
          <!-- Header -->
          <div class="flex grow items-center justify-around pb-2">
            <ImoButton
              v-if="viewMode !== 'years'"
              variant="tertiary"
              @click.prevent="handleArrowClick(-1)"
              icon="arrow-left-02"
            />
            <p
              v-if="viewMode === 'days'"
              id="currentMonth"
              class="text-primary-90 cursor-pointer text-base font-medium"
              @click="toggleMonthView"
            >
              {{ currentMonth }}
            </p>
            <p
              v-else-if="viewMode === 'months'"
              id="currentYear"
              class="text-primary-90 cursor-pointer text-base font-medium"
              @click="toggleYearView"
            >
              {{ currentDate.getFullYear() }}
            </p>
            <p
              v-else-if="viewMode === 'years'"
              id="currentYear"
              class="text-primary-90 cursor-pointer text-base font-medium"
            >
              {{ currentDate.getFullYear() }}
            </p>
            <ImoButton
              v-if="viewMode !== 'years'"
              variant="tertiary"
              @click.prevent="handleArrowClick(1)"
              icon="arrow-right-01"
            />
          </div>

          <!-- Renderização de Dias, Meses ou Anos -->
          <div
            v-if="viewMode === 'days'"
            class="grid grid-cols-7 place-items-center font-normal capitalize"
          >
            <!-- Dias da semana -->
            <div
              v-for="day in daysOfWeek"
              :key="day"
              class="text-neutral-60 flex h-[20px] w-full max-w-[40px] items-center justify-center text-sm font-medium"
            >
              {{ day }}
            </div>

            <!-- Dias do mês -->
            <div
              v-for="(day, index) in renderCalendar()"
              :key="index"
              :class="day.className"
              :data-date="day.dayString"
              @click="handleDayClick(day.dayString)"
            >
              {{ day.day }}
            </div>
          </div>

          <div
            v-if="viewMode === 'months'"
            class="grid grid-cols-3 place-content-start place-items-center gap-2"
          >
            <div
              v-for="(month, index) in renderMonths()"
              :key="index"
              :class="month.className"
              @click="selectMonth(month.monthIndex)"
            >
              {{ month.name }}
            </div>
          </div>
          <div
            v-if="viewMode === 'years'"
            class="grid max-h-[240px] grid-cols-4 place-content-start place-items-center gap-2 overflow-y-auto"
          >
            <div
              v-for="year in renderYears()"
              :key="year.name"
              :class="year.className"
              @click.stop="selectYear(year.name)"
            >
              {{ year.name }}
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<style scoped>
@reference "@/assets/style/main.css";

.input {
  @apply disabled:bg-neutral-10 disabled:border-neutral-30 disabled:text-neutral-40 text-primary-90;
}

.input:focus-visible {
  @apply border-[#4c94ff73];
  box-shadow: 0px 0px 0px 3px #4c94ff73;
}

.error {
  @apply bg-error-10 border-error-60 text-error-60;
  box-shadow: 0px 0px 0px 3px #f008024d;
}
</style>
